<template>
  <BaseCard
    v-if="remoteExports.length"
    :heading="$t('export_page.export_table_heading')"
  >
    <BaseTable
      :headers="tableHeaders"
      :rows="remoteExports.map((remoteExport: any) => ({
        id: remoteExport.id,
        cells: [
          filters.capitalize(labels.find((label) => label.value === remoteExport.exportType)?.label || ''),
          multiLocation ? (remoteExport.dataScope === 'GLOBAL' ? t('global.entire_company') : filters.capitalize(locations.find((location) => location.id === remoteExport.locationId)?.internalName || '')) : undefined,
          remoteExport.fileFormat,
          remoteExport.month && remoteExport.year ? filters.date(dayjs().month(remoteExport.month - 1).year(remoteExport.year).format(), { format: 'monthYear' }) : t('export_page.lifetime'),
          filters.dateTime(remoteExport.createdAt),
          filters.date(remoteExport.expiresAt),
        ].filter((cell) => cell !== undefined),
        actions: [{
          name: 'download',
          icon: 'download',
          label: t('global.actions.download'),
        }],
      }))"
      @action="onAction"
      v-test="'export-remote-export-list'"
    />
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import gql from 'graphql-tag';
import { useI18n } from 'vue-i18n';
import { usePagination } from '@/apollo/pagination';
import dayjs from '@/dayjs';
import eventBus from '@/event-bus';
import filters from '@/filters';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';

const { locations } = useLocationsStore();
const { multiLocation } = useCompanyStore();

const { remoteExports, firstFetchFinished } = usePagination({
  fieldName: 'remoteExports',
  query: gql`
    query getRemoteExports {
      remoteExports {
        createdAt
        dataScope
        expiresAt
        exportType
        fileFormat
        id
        locationId
        month
        url
        year
      }
    }
  `,
  options: {
    pollInterval: 20000,
  },
});

watch(firstFetchFinished, () => {
  if (firstFetchFinished.value) {
    watch(
      () => remoteExports.value.length,
      (newLength, oldLength) => {
        if (newLength > oldLength) {
          eventBus.$emit('export-completed');
        }
      },
    );
  }
});

const onAction = (action: { id: number; option: string }) => {
  if (action.option === 'download') {
    window.open(
      remoteExports.value.find(
        (remoteExport: any) => remoteExport.id === action.id,
      )?.url,
      '_blank',
    );
  }
};

const { t } = useI18n();

const tableHeaders = computed(() => {
  return [
    {
      label: t('export_page.report_information'),
    },
    {
      label: t('global.items.location', 1),
      hide: !multiLocation,
    },
    {
      label: t('export_page.file_type'),
    },
    {
      label: t('global.period'),
    },
    {
      label: t('global.created'),
    },
    {
      label: t('export_page.expiry_date'),
    },
  ]
    .filter((header) => !header.hide)
    .map((header) => header.label);
});

const labels = [
  {
    value: 'appointments',
    label: t('global.items.appointment', 2),
  },
  {
    value: 'absences',
    label: t('global.items.absence', 2),
  },
  {
    value: 'customers',
    label: `${t('global.items.customer', 2)} - ${t('export_page.basic_data')}`,
  },
  {
    value: 'customers_enriched',
    label: `${t('global.items.customer', 2)} - ${t('export_page.all_data')}`,
  },
  {
    value: 'products',
    label: t('global.items.product', 2),
  },
  {
    value: 'current_product_stock',
    label: t('export_page.data_types.current_product_stock'),
  },
  {
    value: 'treatment_reports',
    label: t('global.items.treatment_report', 2),
  },
  {
    value: 'tips',
    label: t('reports.totals.pos_tips'),
  },
  {
    value: 'deleted_appointments',
    label: t('export_page.data_types.deleted_appointments'),
  },
];
</script>
